/**
 * IELTS V2 Writing — Task list + Workspace + Results
 *
 * Routes:
 *   /resources/ielts/writing                   → Task browser
 *   /resources/ielts/writing/workspace/:aid    → Writing workspace
 *   /resources/ielts/writing/results/:aid      → Results / Feedback centre
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, PenSquare, Save, Clock, ChevronRight, ArrowLeft, Award,
  AlertTriangle, CheckCircle2, XCircle, RotateCcw, Compass, Target,
  BarChart3, TrendingUp, TrendingDown, Sparkles,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });
const DISCLAIMER = 'This AI-assisted assessment is provided by Winhouse Tutors for practice and preparation. It is not an official IELTS result and may differ from an assessment by a qualified human examiner.';
const AUTOSAVE_MS = 1500;

const CRIT_LABEL = {
  task_achievement: 'Task Achievement',
  task_response: 'Task Response',
  coherence_cohesion: 'Coherence & Cohesion',
  lexical_resource: 'Lexical Resource',
  grammatical_range_accuracy: 'Grammatical Range & Accuracy',
};
function critLabel(k) { return CRIT_LABEL[k] || (k || '').replace(/_/g, ' '); }

/* ================================================================== */
export function IeltsWritingHubPage() {
  const [menu, setMenu] = useState(false);
  const [variant, setVariant] = useState(() => localStorage.getItem('ielts_variant') || 'academic');
  const [task, setTask] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => { localStorage.setItem('ielts_variant', variant); }, [variant]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/api/ielts/v2/writing/tasks?variant=${variant}&task=${task}`);
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  }, [variant, task]);
  useEffect(() => { load(); }, [load]);

  const openTask = async (tid) => {
    try {
      const r = await ax.post('/api/ielts/v2/writing/attempts/start', { task_id: tid });
      nav(`/resources/ielts/writing/workspace/${r.data.attempt.id}`);
    } catch (e) {
      if (e?.response?.status === 401) { nav('/login?redirect=/resources/ielts/writing'); return; }
      toast.error(e?.response?.data?.detail || 'Could not start task');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation mobileMenuOpen={menu} setMobileMenuOpen={setMenu} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-16">
        <button onClick={() => nav('/resources/ielts')}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to IELTS Hub
        </button>
        <div className="mt-3 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white p-5 md:p-6"
          data-testid="ielts-writing-hub-hero">
          <div className="text-[11px] uppercase tracking-widest text-emerald-100 font-bold">IELTS Writing · Practice with AI Feedback</div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">Winhouse IELTS Writing Practice</h1>
          <p className="text-sm text-emerald-50 mt-1 max-w-2xl">
            Choose a task, write your response, and receive an instant AI-assisted
            estimated practice band with criterion feedback and sentence-level
            coaching. Every attempt is preserved in your IELTS practice history.
          </p>
          <div className="mt-2 text-[11px] text-emerald-50 italic">{DISCLAIMER}</div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="rounded-full bg-white border border-slate-200 p-1 flex">
            {['academic', 'general_training'].map((v) => (
              <button key={v} onClick={() => setVariant(v)}
                data-testid={`ielts-writing-variant-${v}`}
                className={`px-3 py-1 rounded-full text-xs font-bold transition ${
                  variant === v ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                {v === 'academic' ? 'Academic' : 'General Training'}
              </button>
            ))}
          </div>
          <div className="rounded-full bg-white border border-slate-200 p-1 flex">
            {[1, 2].map((t) => (
              <button key={t} onClick={() => setTask(t)}
                data-testid={`ielts-writing-task-${t}`}
                className={`px-3 py-1 rounded-full text-xs font-bold transition ${
                  task === t ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                Task {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ielts-writing-task-list">
          {loading ? (
            <div className="col-span-full text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="col-span-full text-center py-8 text-slate-500 italic">No tasks available yet.</div>
          ) : (
            items.map((t) => (
              <button key={t.id} onClick={() => openTask(t.id)}
                data-testid={`ielts-writing-open-${t.id}`}
                className="text-left rounded-2xl bg-white border border-slate-200 hover:border-emerald-500 hover:shadow p-4 transition">
                <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">
                  Task {t.task_number} · {t.visual_type ? t.visual_type.replace('_', ' ') : (t.letter_tone || 'essay')}
                </div>
                <div className="text-lg font-bold text-[#172033] mt-1">{t.title}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Minimum {t.minimum_words} words · {t.recommended_time_minutes} min recommended
                </div>
                <div className="mt-3 text-xs text-emerald-600 font-bold inline-flex items-center gap-1">
                  Open task <ChevronRight className="h-3.5 w-3.5" />
                </div>
              </button>
            ))
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ================================================================== */
export function IeltsWritingWorkspacePage() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState('idle');
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const saveTimer = useRef(null);
  const startedRef = useRef(Date.now());

  const load = useCallback(async () => {
    try {
      const r = await ax.get(`/api/ielts/v2/writing/attempts/${aid}`);
      const a = r.data.attempt;
      setAttempt(a);
      setText(a.response || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to load attempt');
    }
  }, [aid]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  const wc = useMemo(() => (text.trim() ? text.trim().split(/\s+/).length : 0), [text]);
  const min = attempt?.minimum_words || 250;
  const belowMin = wc < min;

  const queueSave = useCallback((newText) => {
    setSaving('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await ax.post(`/api/ielts/v2/writing/attempts/${aid}/draft`, { response: newText });
        setSaving('saved');
      } catch { setSaving('offline'); }
    }, AUTOSAVE_MS);
  }, [aid]);

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    queueSave(v);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await ax.post(`/api/ielts/v2/writing/attempts/${aid}/draft`, { response: text });
      // Long call — up to 90s
      await ax.post(`/api/ielts/v2/writing/attempts/${aid}/submit`, {}, { timeout: 100000 });
      let mockCtx = null;
      try { mockCtx = JSON.parse(localStorage.getItem('ielts_mock_context') || 'null'); } catch { mockCtx = null; }
      if (mockCtx?.mockId && (mockCtx.section === 'writing_t1' || mockCtx.section === 'writing_t2')) {
        nav(`/resources/ielts/mock/session/${mockCtx.mockId}`);
      } else {
        nav(`/resources/ielts/writing/results/${aid}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
      setSubmitting(false);
    }
  };

  if (!attempt) return <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const isAcademicT1Visual = attempt.task_number === 1 && attempt.variant === 'academic';

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 text-slate-100 overflow-y-auto"
      data-testid="ielts-writing-workspace">
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-700/40 px-4 md:px-8 py-3 flex items-center gap-3 flex-wrap">
        <div className="text-[10px] md:text-xs font-black tracking-widest text-emerald-300">WINHOUSE · IELTS WRITING</div>
        <span className="text-xs text-slate-400 hidden md:inline">
          {attempt.variant === 'academic' ? 'Academic' : 'General Training'} · Task {attempt.task_number}
        </span>
        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-xs">
            <Save className="h-3.5 w-3.5" />
            <span data-testid="ielts-writing-save-chip"
              className={saving === 'offline' ? 'text-rose-400' : (saving === 'saving' ? 'text-amber-300' : 'text-emerald-300')}>
              {saving === 'saving' ? 'Saving…' : saving === 'offline' ? 'Reconnecting…' : 'Saved'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1.5 text-xs font-bold tabular-nums">
            <Clock className="h-3.5 w-3.5 text-emerald-300" /> {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
          </div>
          <button onClick={() => nav('/resources/ielts/writing')}
            className="text-xs text-slate-400 hover:text-slate-100">Exit</button>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row max-w-[1600px] mx-auto w-full">
        {/* Prompt */}
        <div className="lg:w-1/2 lg:border-r lg:border-slate-700/40 px-4 md:px-6 py-5 lg:py-6"
          data-testid="ielts-writing-prompt">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
            {attempt.variant === 'academic' ? 'Academic' : 'General Training'} Writing · Task {attempt.task_number}
          </div>
          <div className="text-lg md:text-2xl font-black">{attempt.task_title}</div>
          <div className="mt-3 text-xs text-emerald-300 font-bold uppercase tracking-widest">
            Minimum {attempt.minimum_words} words · {attempt.recommended_time_minutes} min recommended
          </div>
          <div className="mt-3 rounded-lg bg-slate-900/60 border border-slate-700/40 p-4 max-h-[70vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-slate-200">
            {attempt.task_prompt}
          </div>
          {isAcademicT1Visual && (
            <div className="mt-2 text-[11px] text-amber-300 italic">
              Visual data is described above. Winhouse authored graphics will
              replace this description without changing the grading.
            </div>
          )}
        </div>
        {/* Editor */}
        <div className="lg:w-1/2 px-4 md:px-6 py-5 lg:py-6">
          <div className="flex items-baseline justify-between">
            <div className="text-xs text-slate-400">Your response</div>
            <div className="text-[11px]">
              <span data-testid="ielts-writing-word-count"
                className={belowMin ? 'text-amber-400 font-bold' : 'text-emerald-300 font-bold'}>
                {wc} / {min} words
              </span>
              {belowMin && <span className="ml-1 text-amber-400"> · below minimum</span>}
            </div>
          </div>
          <textarea value={text} onChange={onChange}
            data-testid="ielts-writing-textarea"
            spellCheck
            className="mt-2 w-full min-h-[60vh] bg-slate-950/60 border border-slate-700 focus:border-emerald-400 rounded-lg p-4 text-sm text-slate-100 placeholder-slate-500 outline-none leading-relaxed"
            placeholder="Write your response here. Autosave runs after every pause." />
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button onClick={() => setConfirm(true)}
              disabled={submitting}
              data-testid="ielts-writing-submit"
              className="px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-sm font-black inline-flex items-center gap-2">
              <PenSquare className="h-4 w-4" /> Submit for AI Feedback
            </button>
            <div className="text-[11px] text-slate-500">Your work is saved automatically.</div>
          </div>
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[110] bg-slate-950/70 flex items-center justify-center px-5">
          <div className="max-w-md w-full rounded-2xl bg-slate-900 border border-slate-700 p-6">
            <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold">Submit for AI Feedback</div>
            <h2 className="text-xl font-black mt-1">Submit Writing Response?</h2>
            <p className="text-sm text-slate-300 mt-2 leading-relaxed">
              Once submitted, this attempt will be assessed and preserved in your
              IELTS practice history. Your response becomes immutable — for
              revisions, use <b>Improve My Response</b> from the results page.
            </p>
            {belowMin && (
              <div className="mt-3 rounded-md bg-amber-950/40 border border-amber-700/40 p-3 text-xs text-amber-200 flex gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                Your response is only {wc} words — below the recommended minimum of {min}. This will be flagged in the AI assessment.
              </div>
            )}
            <div className="mt-4 flex items-center gap-3">
              <button onClick={() => setConfirm(false)} disabled={submitting}
                className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-sm">Continue Writing</button>
              <button onClick={submit} disabled={submitting}
                data-testid="ielts-writing-confirm-submit"
                className="ml-auto px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-black inline-flex items-center gap-2">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Grading…</> : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
export function IeltsWritingResultsPage() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regrading, setRegrading] = useState(false);
  const [improving, setImproving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/api/ielts/v2/writing/attempts/${aid}`);
      setData(r.data.attempt);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to load results');
    } finally { setLoading(false); }
  }, [aid]);
  useEffect(() => { load(); }, [load]);

  const retryAssess = async () => {
    setRegrading(true);
    try {
      await ax.post(`/api/ielts/v2/writing/attempts/${aid}/assess`, {}, { timeout: 100000 });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Retry failed'); }
    finally { setRegrading(false); }
  };

  const improve = async () => {
    setImproving(true);
    try {
      const r = await ax.post(`/api/ielts/v2/writing/attempts/${aid}/improve`, {});
      nav(`/resources/ielts/writing/workspace/${r.data.attempt.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create new attempt'); }
    finally { setImproving(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50"><Navigation mobileMenuOpen={menu} setMobileMenuOpen={setMenu} /><div className="pt-32 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…</div><Footer /></div>;
  if (!data) return null;

  const env = data.ai_assessment || {};
  const status = data.ai_assessment_status;
  const cs = env.criterion_scores || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation mobileMenuOpen={menu} setMobileMenuOpen={setMenu} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-16">
        <button onClick={() => nav('/resources/ielts/writing')}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid="ielts-writing-results-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Writing Practice
        </button>

        {/* Failure state */}
        {status !== 'completed' && (
          <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-6"
            data-testid="ielts-writing-results-failed">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-amber-900">Your response has been saved. Automated feedback is temporarily unavailable.</div>
                <div className="text-xs text-slate-700 mt-1">You can retry the AI assessment without rewriting your essay. Your original submission is preserved.</div>
                {data.ai_assessment_error && (
                  <div className="text-[11px] text-slate-500 mt-1">Reason: {data.ai_assessment_error}</div>
                )}
                <button onClick={retryAssess} disabled={regrading}
                  data-testid="ielts-writing-results-retry-assess"
                  className="mt-3 px-4 py-2 rounded-full bg-amber-600 hover:bg-amber-500 text-white text-sm font-black inline-flex items-center gap-2 disabled:opacity-60">
                  {regrading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Retry Assessment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success state */}
        {status === 'completed' && (
        <>
          <div className="mt-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 p-6 md:p-8 text-white shadow-xl"
            data-testid="ielts-writing-results-headline">
            <div className="text-[11px] uppercase tracking-widest text-emerald-100 font-bold">
              Winhouse Estimated IELTS Writing Practice Band
            </div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="text-6xl md:text-7xl font-black tabular-nums" data-testid="ielts-writing-results-band">
                  {(env.estimated_band ?? 0).toFixed(1)}
                </div>
                <div className="text-sm text-emerald-50 mt-1">{data.task_title}</div>
                <div className="text-[11px] text-emerald-50 mt-1">
                  {env.word_count} words · Task {data.task_number} · Confidence: <b>{env.assessment_confidence}</b>
                </div>
                {env.assessment_confidence === 'limited' && env.limited_reasons?.length > 0 && (
                  <div className="mt-2 rounded bg-amber-900/40 border border-amber-500/40 p-2 text-[11px] leading-tight">
                    <b>Limited assessment confidence:</b>
                    <ul className="list-disc list-inside mt-1">
                      {env.limited_reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs text-emerald-100 uppercase tracking-widest font-bold mb-1">Criteria</div>
                <div className="space-y-1.5" data-testid="ielts-writing-results-criteria">
                  {Object.entries(cs).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      <div className="flex-1 text-emerald-50">{critLabel(k)}</div>
                      <div className="tabular-nums font-bold w-10 text-right">{Number(v).toFixed(1)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button onClick={improve} disabled={improving}
                data-testid="ielts-writing-results-improve"
                className="px-5 py-2 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 text-sm font-black inline-flex items-center gap-2 disabled:opacity-60">
                {improving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Improve My Response
              </button>
              <button onClick={retryAssess} disabled={regrading}
                data-testid="ielts-writing-results-retry-assess"
                className="px-4 py-2 rounded-full bg-emerald-800/40 hover:bg-emerald-800/60 text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60">
                {regrading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Retry Assessment
              </button>
            </div>
            <div className="mt-3 text-[11px] text-emerald-50 italic">{DISCLAIMER}</div>
          </div>

          {/* Summary */}
          {env.summary && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <SummaryCard title="Strongest" icon={<TrendingUp className="h-4 w-4 text-emerald-600" />}
                value={critLabel(env.summary.strongest_criterion)} accent="text-emerald-600" testid="ielts-writing-summary-strongest" />
              <SummaryCard title="Biggest Opportunity" icon={<TrendingDown className="h-4 w-4 text-rose-600" />}
                value={critLabel(env.summary.biggest_opportunity)} accent="text-rose-600" testid="ielts-writing-summary-focus" />
              <SummaryCard title="Focus Next Response" icon={<Compass className="h-4 w-4 text-indigo-600" />}
                value={env.summary.focus_for_next_response} accent="text-indigo-700" testid="ielts-writing-summary-next"
                small />
            </div>
          )}

          {/* Strengths + Improvements */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <ListCard title="What you did well" items={env.strengths} tone="emerald" testid="ielts-writing-strengths" />
            <ListCard title="Areas to improve" items={env.improvement_areas} tone="rose" testid="ielts-writing-improvements" />
          </div>

          {/* Sentence feedback */}
          {env.sentence_feedback?.length > 0 && (
            <div className="mt-4 rounded-xl bg-white border border-slate-200 p-4 md:p-6"
              data-testid="ielts-writing-sentence-feedback">
              <div className="text-sm font-bold text-[#172033] flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-emerald-500" /> Sentence-level coaching
              </div>
              <div className="mt-3 space-y-3">
                {env.sentence_feedback.map((s, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                    <div className="text-[11px] uppercase tracking-widest text-rose-600 font-bold">Your sentence</div>
                    <div className="text-sm italic text-slate-800 mt-1">"{s.your_sentence}"</div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mt-2">Issue</div>
                    <div className="text-sm text-slate-700">{s.issue}</div>
                    <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold mt-2">Suggested revision</div>
                    <div className="text-sm text-emerald-800 font-medium">"{s.suggested_revision}"</div>
                    <div className="text-[11px] uppercase tracking-widest text-indigo-600 font-bold mt-2">Why this is stronger</div>
                    <div className="text-sm text-slate-700">{s.why}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grammar + Vocab */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {env.grammar_patterns?.length > 0 && (
              <div className="rounded-xl bg-white border border-slate-200 p-4"
                data-testid="ielts-writing-grammar">
                <div className="text-sm font-bold text-[#172033]">Grammar patterns</div>
                <div className="mt-2 space-y-2">
                  {env.grammar_patterns.map((g, i) => (
                    <div key={i} className="text-xs text-slate-700">
                      <b>{g.pattern}</b>{g.occurrences_estimate ? ` · ~${g.occurrences_estimate} time(s)` : ''}
                      {g.example && <div className="italic text-slate-500">"{g.example}"</div>}
                      {g.fix && <div className="text-emerald-700">→ {g.fix}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {env.vocabulary_analysis && (
              <div className="rounded-xl bg-white border border-slate-200 p-4"
                data-testid="ielts-writing-vocab">
                <div className="text-sm font-bold text-[#172033]">Vocabulary analysis</div>
                <div className="text-xs text-slate-700 mt-1">{env.vocabulary_analysis.range}</div>
                {env.vocabulary_analysis.notes?.length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-xs text-slate-700 space-y-0.5">
                    {env.vocabulary_analysis.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                )}
                {env.vocabulary_analysis.suggested_alternatives?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Try instead</div>
                    <ul className="text-xs text-slate-700 mt-1 space-y-0.5">
                      {env.vocabulary_analysis.suggested_alternatives.map((s, i) => (
                        <li key={i}>"{s.word_or_phrase}" → <b className="text-emerald-700">"{s.instead_try}"</b></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Priority + Recommendation */}
          {(env.priority_improvements?.length > 0 || env.recommended_next_practice) && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {env.priority_improvements?.length > 0 && (
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4"
                  data-testid="ielts-writing-priority">
                  <div className="text-sm font-bold text-indigo-900 flex items-center gap-1"><Target className="h-4 w-4" /> Priority improvements</div>
                  <ol className="mt-2 list-decimal list-inside text-xs text-slate-800 space-y-1">
                    {env.priority_improvements.map((p, i) => <li key={i}>{p}</li>)}
                  </ol>
                </div>
              )}
              {env.recommended_next_practice && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4"
                  data-testid="ielts-writing-recommendation">
                  <div className="text-sm font-bold text-emerald-900 flex items-center gap-1"><Compass className="h-4 w-4" /> Recommended next practice</div>
                  <div className="mt-2 text-sm font-bold text-[#172033]">
                    {critLabel(env.recommended_next_practice.focus)}
                  </div>
                  <div className="text-xs text-slate-700 mt-1">{env.recommended_next_practice.reason}</div>
                </div>
              )}
            </div>
          )}
        </>
        )}
      </div>
      <Footer />
    </div>
  );
}

function SummaryCard({ title, icon, value, accent, small, testid }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">{icon}{title}</div>
      <div className={`mt-1 ${small ? 'text-sm font-bold' : 'text-lg font-black'} ${accent}`}>{value || '—'}</div>
    </div>
  );
}

function ListCard({ title, items, tone, testid }) {
  const cls = tone === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-rose-50 border-rose-200 text-rose-900';
  return (
    <div className={`rounded-xl border p-4 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold">{title}</div>
      {items?.length > 0 ? (
        <ul className="mt-2 text-sm text-slate-800 space-y-1">
          {items.map((s, i) => <li key={i}>{tone === 'emerald' ? '✓' : '→'} {s}</li>)}
        </ul>
      ) : <div className="text-xs italic text-slate-500 mt-2">No specific items identified.</div>}
    </div>
  );
}
